import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered,
  Quote, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Code, Minus
} from "lucide-react";

const ToolbarButton = ({ active, onClick, title, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`p-2 rounded-md transition-colors ${
      active ? "bg-orange-primary text-white" : "text-gray-dark hover:bg-gray-light"
    }`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-6 bg-gray-200 mx-1" aria-hidden="true" />;

/**
 * Minimal Tiptap-based rich-text editor for blog content. Stores its output
 * as serialized HTML so the public blog can render it directly via
 * `dangerouslySetInnerHTML` (Tiptap's HTML output is sanitized at the
 * schema level — no arbitrary tags).
 */
export const RichTextEditor = ({ value, onChange, placeholder = "Write your post…" }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      Placeholder.configure({ placeholder })
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML())
  });

  // Keep editor content in sync when `value` changes externally (e.g. switching
  // between editing different posts).
  React.useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt("Link URL");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  const insertImage = () => {
    const url = window.prompt("Image URL (paste a hosted image link)");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-light">
        <ToolbarButton title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></ToolbarButton>
        <ToolbarButton title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></ToolbarButton>
        <ToolbarButton title="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={16} /></ToolbarButton>
        <ToolbarButton title="Inline code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}><Code size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton title="Heading 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={16} /></ToolbarButton>
        <ToolbarButton title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></ToolbarButton>
        <ToolbarButton title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></ToolbarButton>
        <ToolbarButton title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></ToolbarButton>
        <ToolbarButton title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={16} /></ToolbarButton>
        <ToolbarButton title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton title="Insert link" active={editor.isActive("link")} onClick={setLink}><LinkIcon size={16} /></ToolbarButton>
        <ToolbarButton title="Insert image" onClick={insertImage}><ImageIcon size={16} /></ToolbarButton>
        <Divider />
        <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo size={16} /></ToolbarButton>
        <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo size={16} /></ToolbarButton>
      </div>
      <EditorContent editor={editor} className="prose-editor px-4 py-3 min-h-[300px] max-h-[600px] overflow-y-auto focus-within:outline-none" />
    </div>
  );
};

export default RichTextEditor;
